/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   navRoutes.ts
 *     Description     :   Navigation route registry — the NavRoute[] array that NavSidebar renders, in
 *                         sidebar order. `adminOnly` entries appear only for Administrators.
 *     Module          :   005-web-shell
 *     Component Type  :   Configuration
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-23      Peter Dobler        Initial creation
 *     1.1         2026-06-26      Peter Dobler        Data-driven sidebar order (Dashboard, MCC, Remove Pharmacy, Users, Settings); adminOnly entries
 *     1.2         2026-07-27      Peter Dobler        Spec 031 (T092) — the Universal mapping
 *                                                     workbench registered as a hidden per-migration
 *                                                     route, paired with router.tsx per ui-screens
 *                                                     §3.2. Sidebar order is unchanged
 *     1.3         2026-10-12      Peter Dobler        Spec 040 (T021) — the Schema Drift entry at
 *                                                     /schema-drift after DataMave Sync, not adminOnly
 *     1.4         2026-10-25      Peter Dobler        Spec 044 (T032) — the Switches entry at
 *                                                     /switches after Schema Drift, not adminOnly
 *     1.5         2026-10-29      Peter Dobler        Spec 046 (T024) — the Schema Management entry
 *                                                     at /schema-management before Schema Drift
 *                                                     (lucide Orbit), not adminOnly
 *     1.6         2026-10-30      Peter Dobler        Spec 046 (T097) — the Schema Drift entry withdrawn: its
 *                                                     comparison is Schema Management's Comparison tab
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   react
 *     Internal        :   nav-types
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     navRoutes
 *
 * =========================================================================================
 */

import { lazy } from "react";
import { LayoutDashboard, Database, DatabaseZap, Orbit, ToggleRight, Trash2, Users as UsersIcon, Settings as SettingsIcon } from "lucide-react";
import type { NavRoute } from "@nextgenmigration/shared/nav-types";

const Dashboard = lazy(() => import("../pages/Dashboard.js").then((m) => ({ default: m.Dashboard })));
const JobMonitor = lazy(() => import("../pages/JobMonitor.js").then((m) => ({ default: m.JobMonitor })));
const MigrationControlCenter = lazy(() => import("../pages/console/MigrationControlCenter.js").then((m) => ({ default: m.MigrationControlCenter })));
const MigrationWizard = lazy(() => import("../pages/wizard/MigrationWizard.js").then((m) => ({ default: m.MigrationWizard })));
const RemovePharmacySection = lazy(() => import("../rp/RemovePharmacySection.js").then((m) => ({ default: m.RemovePharmacySection })));
const DataMaveSyncSection = lazy(() => import("../pages/datamave/DataMaveSyncSection.js").then((m) => ({ default: m.DataMaveSyncSection })));
const SchemaManagementPage = lazy(() =>
  import("../pages/schemaManagement/SchemaManagementPage.js").then((m) => ({ default: m.SchemaManagementPage })),
);
const SwitchesPage = lazy(() => import("../pages/Switches.js").then((m) => ({ default: m.SwitchesPage })));
const Users = lazy(() => import("../pages/Users.js").then((m) => ({ default: m.Users })));
const Settings = lazy(() => import("../pages/Settings.js").then((m) => ({ default: m.Settings })));
// Spec 031 (T092) — the Universal mapping workbench. `ui-screens.md` §3.2 requires this path in BOTH
// this registry and `router.tsx`; see the hidden entry at the bottom of the array for why that is not
// redundant.
const MapSourceTables = lazy(() =>
  import("../pages/console/universal/MapSourceTables.js").then((m) => ({ default: m.MapSourceTables })),
);

// Sidebar order is the array order of the showInNav entries below.
export const navRoutes: NavRoute[] = [
  {
    path: "/dashboard",
    label: "Dashboard",
    element: Dashboard,
    icon: LayoutDashboard,
    showInNav: true,
  },
  {
    path: "/migrations",
    label: "Migration Control Center",
    element: MigrationControlCenter,
    icon: Database,
    showInNav: true,
  },
  {
    // Admin-gated (spec 016, folded-in topology) — the Remove Pharmacy section with per-function
    // screens (Home / Counts / Images / Remove Organization Migration / Removal Audit) nested
    // under /remove-pharmacy/*. Only Administrators see the sidebar entry.
    path: "/remove-pharmacy",
    label: "Remove Pharmacy",
    element: RemovePharmacySection,
    icon: Trash2,
    showInNav: true,
    adminOnly: true,
  },
  {
    // Admin-gated (spec 028) — the DataMave Sync section with two screens (Sync / Drift Report)
    // nested under /datamave-sync/*. An on-demand admin action, not a migration stage.
    path: "/datamave-sync",
    label: "DataMave Sync",
    element: DataMaveSyncSection,
    icon: DatabaseZap,
    showInNav: true,
    adminOnly: true,
  },
  {
    // Spec 046 — the write catalog and its maintenance loop. Anyone reads it; deciding a finding or
    // changing an entry is an Administrator's, which the server enforces, so the entry is not adminOnly.
    path: "/schema-management",
    label: "Schema Management",
    element: SchemaManagementPage,
    icon: Orbit,
    showInNav: true,
  },
  {
    // Spec 044 — the feature switches. Everyone reads them (FR-007); only an Administrator changes
    // one, which the server enforces (FR-008, FR-011), so the entry is not adminOnly.
    path: "/switches",
    label: "Switches",
    element: SwitchesPage,
    icon: ToggleRight,
    showInNav: true,
  },
  {
    // Admin-gated user management.
    path: "/users",
    label: "Users",
    element: Users,
    icon: UsersIcon,
    showInNav: true,
    adminOnly: true,
  },
  {
    path: "/settings",
    label: "Settings",
    element: Settings,
    icon: SettingsIcon,
    showInNav: true,
  },

  // ---- Hidden / programmatically-navigated routes (not in the sidebar) ----
  {
    path: "/migrations/:id",
    label: "Migration Control Center",
    element: MigrationControlCenter,
    showInNav: false,
  },
  {
    // Legacy multi-step stepper — superseded by the Migration Control Center console (the faithful
    // MainForm re-port). Kept reachable during transition; hidden from the sidebar.
    path: "/wizard",
    label: "Migration Wizard (legacy)",
    element: MigrationWizard,
    showInNav: false,
  },
  {
    path: "/wizard/migrations/:id",
    label: "Migration Wizard (legacy)",
    element: MigrationWizard,
    showInNav: false,
  },
  {
    path: "/jobs/:id",
    label: "Job Monitor",
    element: JobMonitor,
    showInNav: false,
  },
  {
    /**
     * Spec 031 (T092) — the Universal mapping workbench, `ui-screens.md` §3.2.
     *
     * 🔴 THIS FILE AND `router.tsx` ARE NOT AUTO-SYNCED, IN EITHER DIRECTION. `NavSidebar` filters on
     * `showInNav !== false` before it renders anything, and `router.tsx` never reads the `element`
     * field here — so an entry without a matching `<Route>` is a sidebar link to a 404, and a
     * `<Route>` without an entry here is a screen that appears in no inventory of this application.
     * The design fixes both halves for this path; this is the second half.
     *
     * `showInNav: false` because it is PER-MIGRATION: `/migrations/:id/universal/mapping` has no
     * meaning without an `:id`, and a sidebar link cannot supply one. It is reached from the
     * `Mapping` button on the `import-source` stage row (`ProcessingPanel`), from the Template drawer
     * for a Universal migration, and from the sibling Universal screens' header nav.
     *
     * The three companion Universal routes (`…/source`, `…/parameters`, `…/callability`) are
     * deliberately absent: their components are PANELS taking a required `migrationId` prop, so they
     * are not assignable to this registry's zero-prop `element` type. `router.tsx` wraps each in its
     * own `DetailLayout` chrome, and that composed screen — not the panel — is what the route mounts.
     * Listing them here would mean inventing a second wrapper whose only consumer is a field nothing
     * reads.
     */
    path: "/migrations/:id/universal/mapping",
    label: "Universal Mapping Workbench",
    element: MapSourceTables,
    showInNav: false,
  },
];
